import Humanize from 'humanize-plus';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { BsDownload } from 'react-icons/bs';
import { FaSearch } from 'react-icons/fa';
import SortingDropdown from '../../components/dropdowns/documents/sortMethod/sortMethodDropDown';
import FileTypeIcon from '../../components/filetypeIcon';
import Loading from '../../components/loading';
import PaginationPanel from '../../components/paginations/paginationPanel';
import { Document, File,DocumentSortMethod } from '../../models/document';
import { getDocumentDownloadUrl, getProductManuals } from '../../services/documentService';
import './style.scss';

interface Criteria {
  page: number;
  sku: string;
  sortMethod:number;
}

export default function ProductManualsPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [criteria, setCriteria] = useState<Criteria>({
    page: 1,
    sku: '',
    sortMethod:0
  });
  const [pagedList, setPagedList] = useState<Document>();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const isSelectedAll = useMemo(() => {
    const loadedFiles = pagedList?.files || [];
    const isAllFilesSelected =
      loadedFiles.length > 0 && loadedFiles.every((file) => selectedFiles.includes(file));

    return isAllFilesSelected;
  }, [pagedList, selectedFiles]);

  const downloadFiles = async (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    const fileIds = files.map((file) => file.id);
    const downloadUrl = await getDocumentDownloadUrl(fileIds);

    if (!window.open(downloadUrl)) {
      window.location.href = downloadUrl;
    }
  };

  const toggleSelectFile = (file: File) => {
    const fileIndex = selectedFiles.indexOf(file);
    const isSelected = fileIndex >= 0;

    if (isSelected) {
      // Deselect file.
      selectedFiles.splice(fileIndex, 1);
    } else {
      // Select file.
      selectedFiles.push(file);
    }

    setSelectedFiles([...selectedFiles]);
  };

  const toggleSelectAllFiles = (isSelectAll: boolean) => {
    if (isSelectAll) {
      const allFiles = pagedList?.files || [];
      setSelectedFiles([...allFiles]);
    } else {
      setSelectedFiles([]);
    }
  };

  const loadData = async () => {
    if (!criteria.sku) {
      setPagedList(undefined);
      setSelectedFiles([]);

      return;
    }

    setIsLoading(true);

    try {
      const newPagedList = await getProductManuals({ ...criteria });
      setPagedList(newPagedList);
      setSelectedFiles([]);
    } catch (e) {}

    setIsLoading(false);
  };

  const debouncedSetCriteria = useRef(
    debounce((criteria: Criteria) => {
      setCriteria({
        ...criteria,
      });
    }, 300)
  ).current;

  const handleSearchBarChange = (value: string) => {
    debouncedSetCriteria({ ...criteria,sku: value, page: 1 });
  };

  const handleSortOptionChange=(val:DocumentSortMethod)=>{
    setCriteria({...criteria, sortMethod:Object.keys(DocumentSortMethod).indexOf(val.replaceAll(' ',''))});
  }

  useEffect(() => {
    loadData();

    return () => {
      debouncedSetCriteria.cancel();
    };
  }, [criteria]);

  return (
    <Container>
      <Container className="document-page-header">
        <label>Product Manuals</label>
      </Container>
      <Row>
        <Col md={3}>
          <div className="round-search-input">
            <FaSearch color="white" className="icon" />
            <input
              type="text"
              placeholder="Model Number"
              onChange={(e) => handleSearchBarChange(e.target.value)}
            />
          </div>
        </Col>
        <span className="col-1 pt-1 text-end"><strong>Sort By</strong></span>
              <div className="col-3">        
                <SortingDropdown selectedValue={Object.values(DocumentSortMethod)[criteria.sortMethod]} onSelected={handleSortOptionChange}>
                </SortingDropdown>
            </div>
      </Row>
      <Loading isLoading={isLoading}>
        <div className="document-page-list mt-5">
          <Row className="border-bottom pb-3">
            <Col sm={7} xs={8} className="fw-bold text-uppercase">
              Name
            </Col>
            <Col sm={1} xs={0} className="d-none d-sm-block fw-bold text-uppercase">
              Size
            </Col>
            <Col sm={1} xs={1}>
              <span
                onClick={() => downloadFiles(selectedFiles)}
                title="DOWNLOAD DOCUMENTS"
                className="cursor-pointer"
              >
                <BsDownload />
              </span>
            </Col>
            <Col sm={3} xs={3}>
              <label>
                Select All{'  '}
                <input
                  type="checkbox"
                  checked={isSelectedAll}
                  onChange={(e) => toggleSelectAllFiles(e.target.checked)}
                />
              </label>
            </Col>
          </Row>
          {pagedList &&
            pagedList.files.length > 0 &&
            pagedList.files.map((file) => {
              const humanizedSize = Humanize.fileSize(file.size);
              const isSelected = selectedFiles.includes(file);

              return (
                <Row key={file.id} className="py-3 border-bottom">
                  <Col sm={7} xs={9}>
                    <div className="d-flex align-items-center">
                      <FileTypeIcon extension={file.extension} style={{ width: 30 }} />
                      <div className="ms-3">
                        <span className="d-block">{file.name}</span>
                        <span className="d-block d-sm-none text-black-50 fs-sm">
                          {humanizedSize}
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col sm={1} xs={0} className="text-black-50 d-none d-sm-block">
                    {humanizedSize}
                  </Col>
                  <Col sm={1} xs={0} className="d-none d-sm-block">
                    <span
                      onClick={() => downloadFiles([file])}
                      title="DOWNLOAD DOCUMENTS"
                      className="cursor-pointer"
                    >
                      <BsDownload />
                    </span>
                  </Col>
                  <Col sm={2} xs={3}>
                    <input
                      type="checkbox"
                      className="ms-4"
                      checked={isSelected}
                      onChange={() => toggleSelectFile(file)}
                    />
                  </Col>
                </Row>
              );
            })}
          {pagedList && pagedList.files.length === 0 && (
            <div className="text-center text-black-50 py-5">Can't find Product Manuals.</div>
          )}
        </div>
        {pagedList && (
          <PaginationPanel
            className="mt-5"
            pageCount={pagedList.pageCount}
            pageIndex={criteria.page}
            onPageIndexChange={(pageIndex) => setCriteria({ ...criteria, page: pageIndex })}
          />
        )}
      </Loading>
    </Container>
  );
}
